/**
 * The POS `kontyBarcodeScanner` seam implementation (redesign P4.5 /
 * TechArchitecture.md § app-components) — the mlkit camera-scan flow extracted verbatim from the pre-kit
 * ProductSearch.vue. Resolves the scanned textual value, or undefined on any
 * non-scan outcome (permission denied, module installing, no barcodes, or a
 * detected barcode that cannot be decoded — the cashier is told about that one).
 */
import { BarcodeScanner } from '@capacitor-mlkit/barcode-scanning';
import { onBarcodeUnreadable } from '@/composables/useConfirm';

export async function scanBarcode(): Promise<string | undefined> {
  let permissions = await BarcodeScanner.checkPermissions();

  if (permissions.camera !== 'granted') {
    permissions = await BarcodeScanner.requestPermissions();
    if (permissions.camera !== 'granted') {
      console.log('Camera permission not granted');
      return;
    }
  }

  // Check if Google Barcode Scanner module is available (Android only)
  const { available } = await BarcodeScanner.isGoogleBarcodeScannerModuleAvailable();

  if (!available) {
    console.log('Installing Google Barcode Scanner module...');
    await BarcodeScanner.installGoogleBarcodeScannerModule();
    console.log('Google Barcode Scanner module installation started. Please try again in a moment.');
    return;
  }

  const result = await BarcodeScanner.scan();

  if (!result.barcodes || result.barcodes.length === 0) return;

  // rawValue is unset when the payload isn't UTF-8; displayValue still carries a
  // usable textual form for those barcodes. If neither decodes, tell the cashier
  // instead of closing the scanner silently.
  const barcode = result.barcodes[0];
  const value = barcode.rawValue || barcode.displayValue;
  if (!value) {
    onBarcodeUnreadable();
    return;
  }

  return value;
}
